import { Box, Button, Chip, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useCallback } from "react";
import { InvoiceItemModel } from "types/generated/models";
import Row from "../../Shared/Row";
import { useAnalytics } from "hooks/useAnalytics";
import { getServiceValue } from "../../../utils/serviceValue";

interface IStatementItemProps {
    invoice: InvoiceItemModel;
}

export default function StatementViewItem({ invoice }: IStatementItemProps) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const { track } = useAnalytics();

    const downloadStatement = useCallback(() => {
        if (!invoice.downloadInvoiceUrl) {
            return;
        }

        track("statement_view_download_clicked", {
            invoice_id: invoice.id,
            invoice_number: invoice.invoiceNumber,
            amount_owing: invoice.amountOwingDecimal,
        });

        globalThis.location.assign(invoice.downloadInvoiceUrl);
    }, [invoice, track]);

    return (
        <Box
            sx={{
                border: "1px solid #D4D4D9",
                borderRadius: "4px",
                padding: "24px",
            }}
            data-test-id={`section_statement_item_${invoice.invoiceNumber}`}
        >
            <Box sx={{ display: "flex", flexDirection: "row", gap: "8px", marginBottom: "8px" }}>
                <Typography variant="subtitle1" fontWeight="bold" data-test-id="text_invoice_date">
                    {invoice.invoiceDate}
                </Typography>
                <Typography variant="subtitle1" data-test-id="text_invoice_number">
                    {`${invoice.statementType} #${invoice.invoiceNumber}`}
                    <Chip
                        sx={{ marginLeft: "8px" }}
                        label={invoice.isPaid ? "Paid" : "Unpaid"}
                        color={invoice.isPaid ? "success" : "default"}
                        size="small"
                        data-test-id="status_chip"
                    />
                </Typography>
            </Box>
            <Box
                sx={{
                    flexDirection: isMobile ? "column" : "row",
                    display: "flex",
                    gap: isMobile ? "24px" : "0",
                    overflow: "hidden",
                    minWidth: 0,
                }}
            >
                <Box
                    sx={isMobile ? { width: "100%", minWidth: 0, overflow: "hidden" } : { flex: "1 1 0", minWidth: 0 }}
                >
                    <Row subtitle="Provider" value={invoice.practitionerName} dataTestIdSuffix="practitioner_name" />
                    <Row subtitle="Service" value={getServiceValue(invoice.services)} dataTestIdSuffix="service" />
                    <Row subtitle="Client" value={invoice.clientName} dataTestIdSuffix="client_name" />
                </Box>
                <Box
                    sx={isMobile ? { width: "100%", minWidth: 0, overflow: "hidden" } : { flex: "1 1 0", minWidth: 0 }}
                >
                    <Row
                        subtitle={`${invoice.statementType} Total`}
                        value={invoice.invoiceTotal}
                        dataTestIdSuffix="invoice_total"
                    />
                    <Row subtitle="Paid" value={invoice.amountPaid} dataTestIdSuffix="amount_paid" />
                    <Row subtitle="Owing" value={invoice.amountOwing} dataTestIdSuffix="amount_owing" />
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-end",
                        alignItems: isMobile ? "flex-start" : "flex-end",
                        gap: "8px",
                    }}
                >
                    <Button
                        sx={{ width: isMobile ? "100%" : "100px" }}
                        size="extraSmall"
                        variant="outlined"
                        onClick={downloadStatement}
                        data-test-id="button_download_invoice"
                    >
                        Download
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}
